import {
  AppBar,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { useCallback, useMemo } from 'react';
import { useForm, FormProvider, Controller, useController } from 'react-hook-form';
import { AssociatedPerson, DealDetail } from '@lib/deals';
//import * as adapter from '../../deals/deal-form/deal-form-adapter';
import * as adapter from './form-adapter';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { thumbStack } from '@lib/common/css';
import { vestResolver } from '@hookform/resolvers/vest';
import { QPValidationSuite } from './validators';
import { useFormSnackbarError, useRecentCallback } from '@lib/common';
import { CompanyEntity, QPAddFormEntity } from '../types';
import {
  QPPersonSelector,
  QPCompanySelector,
  ScrollSectionTitle,
} from '../qp-features/qp-add-entities';
import { FormActions } from './form-actions';
import { amber, blue, green, indigo, purple, red } from '@material-ui/core/colors';
import { ControlledTextField } from 'pages/person-management/person-management-form/person-master-relation-form';
import { KeyboardDatePicker } from '@material-ui/pickers';
import { SourceUrls } from '@pages/company-mapping/company-mapping-form/source-urls';
import { SubSectionTitle } from '@pages/company-mapping/common';
import DescriptionRoundedIcon from '@material-ui/icons/DescriptionRounded';
import EventNoteIcon from '@material-ui/icons/EventNote';
import { QPStatementType } from '../quotes-policies.enums';

type DealFormProps = {
  onSubmit: (deal: QPAddFormEntity) => any;
  onDelete?: (deal: QPAddFormEntity) => any;
  deal: DealDetail;
  createDeal?: boolean;
};

export function QPAddForm({ deal, onSubmit, onDelete = null, createDeal = false }: DealFormProps) {
  const formDeal = useMemo(() => adapter.toForm(deal), [deal]);
  const form = useForm({
    defaultValues: formDeal,
    resolver: vestResolver(QPValidationSuite),
    reValidateMode: 'onSubmit',
  });
  const todaysDate = useMemo(() => new Date(), []);

  const [onFinish] = useRecentCallback(deal => onSubmit(deal));
  const [showComments, setShowComments] = useState(false);

  const formError = useFormSnackbarError();

  const submitForm = form.handleSubmit(onFinish, formError);
  const formGrid = (
    <Grid container spacing={1}>
      <Grid item xs={12} style={{ marginBottom: '50px' }}>
        <Grid item>
          <ScrollSectionTitle
            id="date-type"
            subTitle={'Select the type and date of the statement '}
            icon={<EventNoteIcon fontSize="large" style={{ color: blue[700] }} />}>
            Date and Statement Type
          </ScrollSectionTitle>
        </Grid>

        <Divider />
        <br />
        <Grid item xs={12} style={{ display: 'flex', flexDirection: 'row' }}>
          <Grid item xs={6}>
            <Typography variant="h6" gutterBottom>
              Date:
            </Typography>
            <Controller
              name="statement_date"
              render={({ value, onChange, onBlur }) => (
                <KeyboardDatePicker
                  style={{ marginTop: 0, marginBottom: 0 }}
                  // disableToolbar
                  inputVariant="outlined"
                  variant="inline"
                  name="statement_date"
                  format="dd/MM/yyyy"
                  margin="normal"
                  maxDateMessage="Deal Date cannot be greater than todays date."
                  maxDate={todaysDate}
                  id="date-picker-inline"
                  label="Deal Date"
                  value={value}
                  autoOk
                  onChange={onChange}
                  onBlur={onBlur}
                />
              )}
            />
          </Grid>
          <Grid item xs={6}>
            <Typography variant="h6" gutterBottom>
              Statement Type:
            </Typography>
            <ControlledStatementTypeSelect
              fullWidth
              variant="outlined"
              name={'statement_type'}
              label={'Statement Type'}
              control={form.control}
              // defaultValue={personMasterData?.is_deleted ? true : false}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid item xs={12} style={{ marginBottom: '50px' }}>
        <QPCompanySelector deal={formDeal} />
      </Grid>
      <br />
      <br />
      <Grid item xs={12} style={{ marginBottom: '50px' }}>
        <QPPersonSelector deal={deal} createDeal={createDeal} />
      </Grid>
      <br />
      <br />
      <Grid item xs={12} style={{ marginBottom: '50px' }}>
        <Grid container justify="space-between" style={{ marginRight: '30px' }}>
          <Grid item>
            <SubSectionTitle
              icon={<DescriptionRoundedIcon fontSize="large" style={{ color: red[700] }} />}
              title="Statement"
              subTitle="Add a Statement - Quotes/Policies"
            />
          </Grid>
        </Grid>
        <Divider />
        <br />
        <ControlledTextField
          fullWidth
          variant="outlined"
          name={'statement'}
          label={'Statement'}
          control={form.control}
          defaultValue={''}
          multiline
          rows={5}
        />
      </Grid>
      <br />
       <br />
      <Grid item xs={12} style={{ marginBottom: '50px' }}>
        <Grid container justify="space-between" style={{ marginRight: '30px' }}>
          <Grid item>
            <SubSectionTitle
              icon={<DescriptionRoundedIcon fontSize="large" style={{ color: green[700] }} />}
              title="Sources"
              subTitle="Add source information - Quotes/Policies"
            />
          </Grid>
        </Grid>
        <Divider />
        <br />
        <ControlledTextField
          fullWidth
          variant="outlined"
          name={'source'}
          label={'Sources'}
          control={form.control}
          defaultValue={''}
          multiline
          rows={2}
          inputProps={{
            maxLength: 512,
          }}
          rules={{
            maxLength: {
              value: 512,
              message: 'Sources cannot exceed 512 characters.',
            },
          }}
        />
      </Grid>
      <br />
      <br />
      <Grid item xs={12} style={{ marginBottom: '50px' }}>
        <SourceUrls
          source_urls={
            formDeal.source_urls ? formDeal.source_urls : ''
            // deal.source_urls ? deal.source_urls : ''
          }
        />
      </Grid>
    </Grid>
  );

  const QuotesMappingAppBar = () => {
    return (
      <AppBar
        color="secondary"
        style={{ position: 'sticky', marginBottom: '40px', background: purple[800] }}>
        <Grid
          container
          justify="flex-end"
          alignItems="flex-end"
          spacing={2}
          style={{ margin: 0, width: '100%' }}>
          <Grid item>
            <FormActions createQPRelation={createDeal} qpData={deal} submitForm={submitForm} />
          </Grid>
        </Grid>
      </AppBar>
    );
  };

  return (
    <FormProvider {...form}>
      <QuotesMappingAppBar />
      <form onSubmit={submitForm}>
        <input hidden name="id" ref={form.register} />
        <Grid style={{ position: 'relative' }} container spacing={3}>
          <Grid item xs={showComments ? 9 : 12}>
            {formGrid}
          </Grid>
        </Grid>
        <Grid item>
          <FormActions createQPRelation={createDeal} qpData={deal} submitForm={submitForm} />
        </Grid>
      </form>
    </FormProvider>
  );
}
const ControlledStatementTypeSelect = ({ name, control, ...props }) => {
  const {
    field: { value, onChange },
  } = useController({
    name,
    control,
    defaultValue: null,
  });

  return (
    <TextField
      select
      {...props}
      name={name}
      value={value ?? ''}
      onChange={e => onChange(e.target.value || null)}
      style={{ width: '65%' }}>
      {/* Default option */}
      <MenuItem value="">Select type</MenuItem>

      {/* Enum-driven options */}
      {Object.values(QPStatementType).map(type => (
        <MenuItem key={type} value={type}>
          {type}
        </MenuItem>
      ))}
    </TextField>
  );
};
